import React, { useEffect, useState } from 'react';
import { LeaveApplicationResponse, LeaveStatus } from '../../interfaces/leave.interface';
import { LeaveService } from '../../services/leave.service';
import { UserService } from '../../services/user.service';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    TablePagination,
    Link,
    Autocomplete,
    TextField,
    Tooltip
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { format } from 'date-fns';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { displayUsername } from '../../utils/userUtils';

interface InstitutionUser {
    id: string;
    username: string;
    phone: string;
    type: string;
    isActive: boolean;
}

interface ConfirmDialogProps {
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    onClose: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ open, title, message, onConfirm, onClose }) => {
    const { t } = useTranslation('common');
    return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
            <Typography>{message}</Typography>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>{t('actions.cancel')}</Button>
            <Button onClick={onConfirm} variant="contained" color="primary" autoFocus>
                {t('actions.confirm')}
            </Button>
        </DialogActions>
    </Dialog>
    );
};

const AllLeaveApplications: React.FC = () => {
    const [leaveData, setLeaveData] = useState<LeaveApplicationResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedLeaveId, setSelectedLeaveId] = useState<string | null>(null);
    const toast = useToast();
    const { t } = useTranslation('leave');
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [users, setUsers] = useState<InstitutionUser[]>([]);
    const [selectedUser, setSelectedUser] = useState<InstitutionUser | null>(null);
    const [userInput, setUserInput] = useState('');
    const [userSearchLoading, setUserSearchLoading] = useState(false);

    useEffect(() => {
        if (!userInput.trim()) {
            setUsers([]);
            return;
        }
        const handle = setTimeout(async () => {
            setUserSearchLoading(true);
            try {
                const res = await UserService.getInstitutionUsers(0, 20, undefined, userInput);
                setUsers(res.content);
            } catch (err) {
                toast.showApiError(err, t('allLeaveApplications.errors.searchEmployees'));
            } finally {
                setUserSearchLoading(false);
            }
        }, 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInput]);

    useEffect(() => {
        fetchAllLeaveApplications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, selectedUser?.id]);

    const fetchAllLeaveApplications = async () => {
        try {
            const data = await LeaveService.getAllApplicationsByInstitution(page, rowsPerPage, selectedUser?.id);
            setLeaveData(data);
        } catch (err) {
            toast.showApiError(err, t('allLeaveApplications.errors.fetch'));
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleApproveClick = (id: string) => {
        setSelectedLeaveId(id);
        setApproveDialogOpen(true);
    };

    const handleRejectClick = (id: string) => {
        setSelectedLeaveId(id);
        setRejectDialogOpen(true);
    };

    const handleApproveConfirm = async () => {
        if (!selectedLeaveId) return;
        try {
            await LeaveService.approveLeaveApplication(selectedLeaveId);
            await fetchAllLeaveApplications();
            setApproveDialogOpen(false);
        } catch (err) {
            toast.showApiError(err, t('allLeaveApplications.errors.approve'));
        }
    };

    const handleRejectConfirm = async () => {
        if (!selectedLeaveId) return;
        try {
            await LeaveService.rejectLeaveApplication(selectedLeaveId);
            await fetchAllLeaveApplications();
            setRejectDialogOpen(false);
        } catch (err) {
            toast.showApiError(err, t('allLeaveApplications.errors.reject'));
        }
    };

    const getStatusColor = (status: LeaveStatus) => {
        switch (status) {
            case LeaveStatus.Approved:
                return 'success';
            case LeaveStatus.Rejected:
                return 'error';
            case LeaveStatus.Cancelled:
                return 'warning';
            default:
                return 'default';
        }
    };

    const formatDateTime = (dateString: string) => {
        return format(new Date(dateString), 'dd-MM-yyyy');
    };

    if (loading) return <Typography>{t('loading')}</Typography>;
    if (!leaveData) return <Typography>{t('allLeaveApplications.empty')}</Typography>;

    return (
        <Box sx={{ padding: 3 }}>
            <Typography variant="h5" sx={{ marginBottom: 3 }}>
                {t('allLeaveApplications.title')}
            </Typography>
            <Autocomplete
                options={users}
                getOptionLabel={(option) => displayUsername(option.username)}
                value={selectedUser}
                onChange={(_, newValue) => {
                    setSelectedUser(newValue);
                    setPage(0);
                }}
                onInputChange={(_, value, reason) => {
                    if (reason === 'input') {
                        setUserInput(value);
                    } else if (reason === 'clear') {
                        setUserInput('');
                    }
                }}
                filterOptions={(x) => x}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={userSearchLoading}
                noOptionsText={userInput ? t('allLeaveApplications.noEmployeesFound') : t('allLeaveApplications.typeToSearch')}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t('allLeaveApplications.filterByEmployee')}
                        placeholder={t('allLeaveApplications.searchEmployeesPlaceholder')}
                        size="small"
                    />
                )}
                sx={{ mb: 2, maxWidth: 400 }}
            />
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('allLeaveApplications.columns.employee')}</TableCell>
                                <TableCell>{t('allLeaveApplications.columns.leaveType')}</TableCell>
                                <TableCell>{t('allLeaveApplications.columns.reason')}</TableCell>
                                <TableCell>{t('allLeaveApplications.columns.applicationDate')}</TableCell>
                                <TableCell>{t('allLeaveApplications.columns.startDate')}</TableCell>
                                <TableCell>{t('allLeaveApplications.columns.endDate')}</TableCell>
                                <TableCell>{t('allLeaveApplications.columns.days')}</TableCell>
                                <TableCell>{t('allLeaveApplications.columns.status')}</TableCell>
                                <TableCell>{t('allLeaveApplications.columns.approver')}</TableCell>
                                <TableCell>{t('allLeaveApplications.columns.documents')}</TableCell>
                                <TableCell>{t('allLeaveApplications.columns.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {leaveData.content.map((leave) => (
                                <TableRow key={leave.id}>
                                    <TableCell>{displayUsername(leave.userName)}</TableCell>
                                    <TableCell>{leave.leaveType}</TableCell>
                                    <TableCell sx={{ maxWidth: 260 }}>
                                        <Tooltip title={leave.leaveReason || ''} placement="top-start" arrow>
                                            <Box
                                                sx={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {leave.leaveReason}
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(leave.applicationDate)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(leave.startDate)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(leave.endDate)}</TableCell>
                                    <TableCell>{leave.numberOfDays}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={t(`status.${leave.leaveStatus}`)}
                                            color={getStatusColor(leave.leaveStatus) as any}
                                            size="small"
                                            sx={{ pointerEvents: 'none' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {leave.actionedByName ? (
                                            <Box>
                                                <Typography variant="body2">{displayUsername(leave.actionedByName)}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {leave.actionedAt ? format(new Date(leave.actionedAt), 'dd MMM yyyy, hh:mm a') : ''}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">—</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {leave.documents && leave.documents.length > 0 ? (
                                            <Box>
                                                {leave.documents.map((doc) => (
                                                    <Link
                                                        key={doc.id}
                                                        component="button"
                                                        variant="body2"
                                                        onClick={() => LeaveService.downloadDocument(doc.id, doc.originalFileName)}
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
                                                    >
                                                        <AttachFileIcon fontSize="small" />
                                                        {doc.originalFileName}
                                                    </Link>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">-</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                onClick={() => handleApproveClick(leave.id)}
                                                disabled={leave.leaveStatus !== LeaveStatus.Pending}
                                            >
                                                {t('allLeaveApplications.approve')}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                size="small"
                                                onClick={() => handleRejectClick(leave.id)}
                                                disabled={leave.leaveStatus !== LeaveStatus.Pending}
                                            >
                                                {t('allLeaveApplications.reject')}
                                            </Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={leaveData.totalElements}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </Paper>

            <ConfirmDialog
                open={approveDialogOpen}
                title={t('allLeaveApplications.approveDialogTitle')}
                message={t('allLeaveApplications.approveDialogMessage')}
                onConfirm={handleApproveConfirm}
                onClose={() => setApproveDialogOpen(false)}
            />

            <ConfirmDialog
                open={rejectDialogOpen}
                title={t('allLeaveApplications.rejectDialogTitle')}
                message={t('allLeaveApplications.rejectDialogMessage')}
                onConfirm={handleRejectConfirm}
                onClose={() => setRejectDialogOpen(false)}
            />
        </Box>
    );
};

export default AllLeaveApplications;
